import { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { PurchaseOrderDetailOverviewFormValues } from '../containers/purchase-order-detail-overview/models/purchase-order-detail-form-values';
import { ReceiptNoteType } from '../../../common/models/enums/receipt-note-type';
import { paloDraftNoteIds, usePaloDraftNotices } from '../../../common/components/palo/palo-draft-notices';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePurchaseOrderDetailContext } from '../containers/context/purchase-order-context';

/**
 * Tells the positions which supplier the head currently has.
 *
 * The article search used to read the supplier from the saved order. Since
 * head and positions stand on one page, that is wrong twice over: a new order
 * has nothing saved, so the search was unfiltered, and a changed one filtered
 * on the supplier it had before — articles of another supplier could reach the
 * order, which goes out to that supplier (Prüfbericht Roland Niedermayr,
 * 28.09.2026).
 *
 * The notes of the picked supplier and customer are asked for here too, so
 * they stand in the head before the first save (#2236).
 *
 * It draws nothing; it only reports what the field holds.
 */
export const PaloPurchaseOrderSupplierWatcher = (): null => {
    const { values } = useFormikContext<PurchaseOrderDetailOverviewFormValues>();
    const { setSupplierAccountId, setSupplierAccountName } = usePurchaseOrderDetailContext();
    const supplierId = values.supplierAccount?.id ?? null;
    const supplierName = values.supplierAccount?.name;

    // The notes follow the head as well; the articles stay those saved.
    const savedItems = useAppSelector(state => state.purchaseOrders.entity?.purchaseOrderItems);

    usePaloDraftNotices(
        ReceiptNoteType.PurchaseOrders,
        paloDraftNoteIds({
            accountIds: [values.supplierAccount?.id, values.customerAccount?.id],
            contactIds: [
                values.supplierContact?.id,
                values.customerContact?.id,
                values.customerAvisoContact?.id,
                values.responsibleContact?.id,
            ],
            productIds: (savedItems ?? []).map(item => item.product?.id),
        })
    );

    useEffect(() => {
        setSupplierAccountId?.(supplierId);
        setSupplierAccountName?.(supplierName);

        // Leaves the screen, says nothing: a reader falls back to the saved
        // order, which is right in the wizard where the head is a step of its
        // own.
        return () => {
            setSupplierAccountId?.(undefined);
            setSupplierAccountName?.(undefined);
        };
    }, [setSupplierAccountId, setSupplierAccountName, supplierId, supplierName]);

    return null;
};
